import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import FlippableCard from '@/components/FlippableCard';
import { cardImagePath } from '@/lib/cardImagePath';
import NatalWheel from '@/components/NatalWheel';
import OrnateFrame from '@/components/OrnateFrame';
import Starfield from '@/components/Starfield';
import type { NatalChartData } from '@/api/client';
import i18n from '@/i18n';

/**
 * Marketing capture page — hidden /demo route. Renders the app's UNIQUE
 * features at "marketing scale" (much bigger than they appear in the
 * real app) so they screen-record cleanly into TikTok / Reels videos.
 *
 * Four scenes:
 *   • fan       — animated 78-card fan opening and closing
 *   • spread    — 3-7 cards laid out, cascade-revealing
 *   • natal     — natal-chart wheel with mock planets/aspects
 *   • synastry  — partner planet table sliding in
 *
 * Each scene is selected from a dropdown in the controls panel; URL
 * params `?scene=fan&clean=1` jump straight into a scene with no
 * controls visible (handy for one-take recordings).
 *
 * Why these four: each one is something the competitors in the Tarot
 * TikTok space DON'T have:
 *   • Most "tarot bots" don't have a full illustrated deck (we have 78)
 *   • Real tarologist creators don't have a digital deck on camera
 *   • Astrology features (natal/synastry) are rare in Tarot bots
 *   • Animations like the cascade flip are unique brand signal
 */

interface CardEntry { name_en: string; name_ru: string; }

const MAJORS: CardEntry[] = [
  { name_en: 'The Fool',           name_ru: 'Дурак' },
  { name_en: 'The Magician',       name_ru: 'Маг' },
  { name_en: 'The High Priestess', name_ru: 'Верховная Жрица' },
  { name_en: 'The Empress',        name_ru: 'Императрица' },
  { name_en: 'The Emperor',        name_ru: 'Император' },
  { name_en: 'The Hierophant',     name_ru: 'Иерофант' },
  { name_en: 'The Lovers',         name_ru: 'Влюблённые' },
  { name_en: 'The Chariot',        name_ru: 'Колесница' },
  { name_en: 'Strength',           name_ru: 'Сила' },
  { name_en: 'The Hermit',         name_ru: 'Отшельник' },
  { name_en: 'Wheel of Fortune',   name_ru: 'Колесо Фортуны' },
  { name_en: 'Justice',            name_ru: 'Справедливость' },
  { name_en: 'The Hanged Man',     name_ru: 'Повешенный' },
  { name_en: 'Death',              name_ru: 'Смерть' },
  { name_en: 'Temperance',         name_ru: 'Умеренность' },
  { name_en: 'The Devil',          name_ru: 'Дьявол' },
  { name_en: 'The Tower',          name_ru: 'Башня' },
  { name_en: 'The Star',           name_ru: 'Звезда' },
  { name_en: 'The Moon',           name_ru: 'Луна' },
  { name_en: 'The Sun',            name_ru: 'Солнце' },
  { name_en: 'Judgement',          name_ru: 'Суд' },
  { name_en: 'The World',          name_ru: 'Мир' },
];

const RANK_PAIRS: Array<[string, string]> = [
  ['Ace',    'Туз'],     ['Two',   'Двойка'],   ['Three', 'Тройка'],
  ['Four',   'Четвёрка'], ['Five',  'Пятёрка'],  ['Six',   'Шестёрка'],
  ['Seven',  'Семёрка'],  ['Eight', 'Восьмёрка'], ['Nine',  'Девятка'],
  ['Ten',    'Десятка'],  ['Page',  'Паж'],      ['Knight','Рыцарь'],
  ['Queen',  'Королева'], ['King',  'Король'],
];

const SUIT_PAIRS: Array<[string, string]> = [
  ['Wands',     'Жезлов'],
  ['Cups',      'Кубков'],
  ['Swords',    'Мечей'],
  ['Pentacles', 'Пентаклей'],
];

const MINORS: CardEntry[] = SUIT_PAIRS.flatMap(([suitEn, suitRu]) =>
  RANK_PAIRS.map(([rankEn, rankRu]) => ({
    name_en: `${rankEn} of ${suitEn}`,
    name_ru: `${rankRu} ${suitRu}`,
  })),
);


// ─── Scene metadata ────────────────────────────────────────────────────

type SceneKind = 'intro' | 'fan' | 'spread' | 'natal' | 'synastry' | 'master' | 'diary';

const SCENE_TITLES: Record<SceneKind, { ru: string; en: string; sub_ru: string; sub_en: string }> = {
  intro: {
    ru: 'Заставка',
    en: 'Intro',
    sub_ru: 'Звёзды → название → карта → подпись',
    sub_en: 'Stars → title → card → subtitle',
  },
  fan: {
    ru: 'Полная колода Таро',
    en: 'Full Tarot deck',
    sub_ru: '78 карт · нарисовано специально для проекта',
    sub_en: '78 cards · illustrated for this project',
  },
  spread: {
    ru: 'Три карты',
    en: 'Three cards',
    sub_ru: '3 карты · быстрый ответ',
    sub_en: '3 cards · quick answer',
  },
  natal: {
    ru: 'Натальная карта',
    en: 'Natal chart',
    sub_ru: 'Карта неба в момент твоего рождения',
    sub_en: 'The sky at the moment of your birth',
  },
  synastry: {
    ru: 'Синастрия',
    en: 'Synastry',
    sub_ru: 'Сравнение двух натальных карт',
    sub_en: 'Two natal charts compared',
  },
  master: {
    ru: 'Комната Мастера',
    en: 'Master\'s Room',
    sub_ru: 'AI-таролог отвечает на твои вопросы',
    sub_en: 'AI tarot reader answers your questions',
  },
  diary: {
    ru: 'Дневник',
    en: 'Diary',
    sub_ru: 'История твоих раскладов · AI находит паттерны',
    sub_en: 'Your reading history · AI finds patterns',
  },
};


// ─── localStorage persistence ──────────────────────────────────────────

const LS_SCENE = 'demo_scene';

function readLS<T>(key: string, fallback: T, parse: (s: string) => T): T {
  try {
    const v = localStorage.getItem(key);
    return v ? parse(v) : fallback;
  } catch {
    return fallback;
  }
}


// ─── The screen ────────────────────────────────────────────────────────

export default function Demo() {
  const { i18n: i18nHook } = useTranslation();
  const lang = i18nHook.language.startsWith('ru') ? 'ru' : 'en';

  // Scene selector — also accepts ?scene= URL param for one-take captures.
  const [scene, setScene] = useState<SceneKind>(() => {
    const sp = new URLSearchParams(window.location.search);
    const urlScene = sp.get('scene') as SceneKind | null;
    if (urlScene && ['intro', 'fan', 'spread', 'natal', 'synastry', 'master', 'diary'].includes(urlScene)) {
      return urlScene;
    }
    return readLS<SceneKind>(LS_SCENE, 'spread', (s) => s as SceneKind);
  });
  useEffect(() => {
    try { localStorage.setItem(LS_SCENE, scene); } catch {}
  }, [scene]);

  // Replay key — bumping it remounts whatever scene is current so the
  // animation can be re-recorded without a page reload. Triggered by
  // the button or the R hotkey.
  const [playKey, setPlayKey] = useState(0);

  const [showControls, setShowControls] = useState(true);
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('clean') === '1') setShowControls(false);
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA')) return;
      if (e.key === 'h' || e.key === 'H') setShowControls((v) => !v);
      if (e.key === 'r' || e.key === 'R') setPlayKey((k) => k + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Record mode — `?record=1` strips ALL Demo chrome (header, title,
  // controls, Starfield, page padding) so only the bare scene fills the
  // viewport. Intended for headless-browser video capture: playwright
  // boots with viewport 360×640 + DPR=3, records 1080×1920 native, no
  // upscaling needed. Body becomes pure black so any rounding artefact
  // around the intro panel doesn't show up on the rendered video.
  const recordMode = new URLSearchParams(window.location.search).get('record') === '1';
  useEffect(() => {
    if (!recordMode) return;
    const prev = document.body.style.background;
    document.body.style.background = '#000';
    return () => { document.body.style.background = prev; };
  }, [recordMode]);

  // Fan style — only meaningful when scene === 'fan'. Three variants
  // (sync / wave / deal) so the operator can compare which reads best
  // on video. Default 'wave' — most "alive" of the three.
  const fanStyle: FanStyle = (() => {
    const sp = new URLSearchParams(window.location.search);
    const v = sp.get('fan');
    return v === 'sync' || v === 'wave' || v === 'deal' ? v : 'wave';
  })();

  const title = lang === 'ru' ? SCENE_TITLES[scene].ru : SCENE_TITLES[scene].en;
  const subtitle = lang === 'ru' ? SCENE_TITLES[scene].sub_ru : SCENE_TITLES[scene].sub_en;

  // Record-mode rendering: bare scene on solid black, no header, no
  // title, no controls, no Starfield. Used by scripts/record_intro.py.
  if (recordMode) {
    return (
      <div
        key={`${scene}-${playKey}`}
        className="fixed inset-0 flex items-center justify-center bg-black"
      >
        {scene === 'intro' && <IntroScene />}
        {scene === 'fan' && <FanScene style={fanStyle} />}
        {scene === 'spread' && <SpreadScene lang={lang} />}
        {scene === 'natal' && <NatalScene lang={lang} />}
        {scene === 'synastry' && <SynastryScene lang={lang} />}
        {scene === 'master' && <MasterScene lang={lang} />}
        {scene === 'diary' && <DiaryScene lang={lang} />}
      </div>
    );
  }

  return (
    <>
      <Starfield />

      <main className="relative mx-auto max-w-md px-5 pb-24 pt-2">
        <header className="sticky top-0 z-20 mx-auto flex max-w-md items-center
                           justify-between px-1 pt-3 pb-2 backdrop-blur-sm">
          <div className="flex items-center gap-2">
            <div
              aria-hidden="true"
              className="flex h-11 w-11 items-center justify-center rounded-full
                         border border-gold-500/40 bg-night-900/50 text-xl text-gold-200
                         shadow-[0_0_12px_rgba(232,195,86,0.15)]"
            >
              ←
            </div>
            <div className="group flex items-center gap-2">
              <span className="text-2xl text-gold-400">✦</span>
              <span className="font-serif text-lg tracking-wide text-gold-300">
                Mystic Companion
              </span>
            </div>
          </div>
          <div className="flex items-center">
            <DemoLangToggle />
          </div>
        </header>

        <h1 className="mb-1 mt-2 text-center font-serif text-2xl text-gold-300">
          {title}
        </h1>
        <p className="mb-6 text-center text-xs italic text-gold-300/60">
          {subtitle}
        </p>

        {/* Scene — keyed on playKey so all internal state resets when
            the operator hits Replay. */}
        <div key={`${scene}-${playKey}`}>
          {scene === 'intro' && <IntroScene />}
          {scene === 'fan' && <FanScene style={fanStyle} />}
          {scene === 'spread' && <SpreadScene lang={lang} />}
          {scene === 'natal' && <NatalScene lang={lang} />}
          {scene === 'synastry' && <SynastryScene lang={lang} />}
          {scene === 'master' && <MasterScene lang={lang} />}
          {scene === 'diary' && <DiaryScene lang={lang} />}
        </div>

        {showControls && (
          <div className="mt-6 rounded-xl border border-gold-500/20 bg-night-900/60 p-4 backdrop-blur-sm">
            <p className="mb-3 text-[10px] uppercase tracking-widest text-gold-500/60">
              Управление демо · скрывается с ?clean=1 или клавишей H
            </p>
            <div className="mb-3 flex items-center gap-3">
              <label className="text-xs text-gold-300/70">Сцена:</label>
              <select
                value={scene}
                onChange={(e) => setScene(e.target.value as SceneKind)}
                className="rounded-md border border-gold-500/40 bg-night-900 px-2 py-1 text-sm text-gold-200"
              >
                <option value="intro">Заставка для видео</option>
                <option value="fan">Колода: 78 карт</option>
                <option value="spread">Расклад с каскадным флипом</option>
                <option value="natal">Натальная карта (колесо)</option>
                <option value="synastry">Синастрия (планеты партнёра)</option>
                <option value="master">Комната Мастера (AI-чат)</option>
                <option value="diary">Дневник (история раскладов)</option>
              </select>
            </div>
            <button
              type="button"
              onClick={() => setPlayKey((k) => k + 1)}
              className="rounded-md border border-gold-400 bg-gold-500/20 px-4 py-2 text-sm text-gold-100 transition hover:bg-gold-500/30"
            >
              ↻ Запустить анимацию заново
            </button>
            <p className="mt-3 text-[10px] uppercase tracking-widest text-gold-500/50">
              H — скрыть панель · R — replay анимации · ?scene=fan&clean=1 — прямой заход
            </p>
          </div>
        )}
      </main>
    </>
  );
}


// ─── Scene: Fan animation ──────────────────────────────────────────────

/**
 * 78-card fan that opens, holds, and closes. The "wow" hook for video
 * intros — visually communicates "we have the FULL deck" in ~3 seconds.
 *
 * Layout math: cards spread across a 140° arc centred on vertical
 * (so the fan opens up-and-out like a hand holding cards). Pivot is
 * BELOW the card so the cards spread radially without overlapping
 * their bottoms.
 *
 * Each card is the back illustration — face-down — because:
 *   1. Loading 78 different front images would hammer the network.
 *   2. The visual story is "this is a full sealed deck, look at it" —
 *      face-down is the right metaphor.
 */
/**
 * Deck-showcase scene — Majors carousel + Minors background ribbon.
 *
 * Why this layout and not a fan/grid:
 *   The fan hid card art under overlap. The grid showed all 78 cards
 *   but at postage-stamp size — undistinguishable as illustrations.
 *   The "Три карты" scene proved that ONE big card sells the art.
 *
 *   So this scene leans into that: 22 Majors each shown one-by-one at
 *   "Три карты" size, hero-card-style, ~0.5s per card → 11s total to
 *   walk the whole Major Arcana. Each illustration gets a moment.
 *
 *   Behind the hero, a continuous horizontal ribbon of all 56 Minors
 *   crawls right-to-left, looping forever. It carries the "and there
 *   are MORE" message without distracting from the hero.
 *
 *   Caption underneath the hero card states the maths explicitly:
 *   "22 Старших · 56 Младших · 78 всего" — viewer doesn't have to
 *   count what they're seeing.
 *
 * The 3 style URL params (sync/wave/deal) are no longer meaningful for
 * this layout, but the FanStyle type is preserved for prop signature
 * compatibility with the Demo route's URL parsing.
 */
type FanStyle = 'sync' | 'wave' | 'deal';

function FanScene({ style: _style }: { style: FanStyle }) {
  // Cycle through Majors, one hero card at a time.
  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setHeroIdx((i) => (i + 1) % MAJORS.length);
    }, 500);                                          // 0.5s per card
    return () => clearInterval(id);
  }, []);
  const hero = MAJORS[heroIdx];

  // Background ribbon: render each Minor TWICE in a row so the
  // CSS-animated translateX can loop without a visible seam (the second
  // half slides into place exactly where the first half was).
  const ribbonCards = [...MINORS, ...MINORS];
  const RIBBON_CARD_W = 44;
  const RIBBON_CARD_H = Math.round(RIBBON_CARD_W * (1028 / 768));  // 59
  const RIBBON_GAP = 4;
  // One loop = width of one MINORS pass = N * (W + gap). Animate -X to
  // exactly this distance, then snap back — visually seamless.
  const ribbonLoopPx = MINORS.length * (RIBBON_CARD_W + RIBBON_GAP);

  return (
    <OrnateFrame className="card-glow mb-6 rounded-2xl border border-gold-500/30 bg-night-900/40 px-3 py-5 overflow-hidden">
      {/* Background ribbon — Minors scrolling right-to-left. Positioned
          ABSOLUTELY behind the hero with reduced opacity so it reads as
          atmosphere, not as competing content. */}
      <div
        className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2"
        style={{ opacity: 0.35 }}
      >
        <motion.div
          className="flex"
          style={{ gap: RIBBON_GAP, width: 'max-content' }}
          animate={{ x: [0, -ribbonLoopPx] }}
          transition={{
            duration: 40,                             // 40s for full loop
            ease: 'linear',
            repeat: Infinity,
          }}
        >
          {ribbonCards.map((card, i) => (
            <img
              key={i}
              src={cardImagePath(card.name_en) || '/cards/_back.jpeg'}
              alt=""
              draggable={false}
              style={{
                width: RIBBON_CARD_W,
                height: RIBBON_CARD_H,
                borderRadius: 3,
                flexShrink: 0,
              }}
            />
          ))}
        </motion.div>
      </div>

      {/* Foreground hero — one Major at a time, cross-faded. */}
      <div className="relative flex flex-col items-center" style={{ minHeight: 360 }}>
        <AnimatePresence mode="wait">
          <motion.img
            key={heroIdx}
            src={cardImagePath(hero.name_en) || '/cards/_back.jpeg'}
            alt={hero.name_en}
            draggable={false}
            initial={{ opacity: 0, scale: 0.92, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 1.04, y: -8 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            style={{
              width: 200,
              height: Math.round(200 * (1028 / 768)),  // 267
              borderRadius: 6,
              boxShadow: '0 8px 32px rgba(0,0,0,0.55), 0 0 24px rgba(232,195,86,0.18)',
            }}
          />
        </AnimatePresence>

        <p
          className="mt-4 font-serif italic text-gold-300/85"
          style={{ fontSize: 15, textAlign: 'center' }}
        >
          {hero.name_ru} · {hero.name_en}
        </p>
        <p
          className="mt-1 text-gold-300/55"
          style={{ fontSize: 11, letterSpacing: '0.05em', textAlign: 'center' }}
        >
          22 Старших · 56 Младших · 78 всего
        </p>
      </div>
    </OrnateFrame>
  );
}


// ─── Scene: Intro (video intro reveal sequence) ────────────────────────

/**
 * Branded video-intro reveal. Used as the opening 7 seconds of TikTok
 * /Reels clips, before the actual feature demos start.
 *
 * Two-act structure:
 *
 *   ACT I — "the cosmos appears" (0 → 2.0s)
 *     Black panel. 28 golden star-particles materialise and drift
 *     upward. The viewer reads "starry night, something's coming."
 *
 *   ACT II — "light reveals the poster" (2.0s → 6.5s)
 *     A glowing horizontal beam descends from top to bottom, and a
 *     black curtain retreats with it. As the beam passes each region
 *     of the poster (title → card → subtitle) that region "ignites":
 *
 *       2.0s  beam enters at top
 *       3.0s  beam at ~20% — MYSTIC COMPANION title revealed
 *       4.5s  beam at ~60% — ornate card with The Star/Moon revealed
 *       5.5s  beam at ~95% — bottom subtitle revealed
 *       6.5s+ beam exits, gentle gold-glow pulse on the card holds
 *
 * Variants: ?intro=star → intro-star.jpeg (default, "Star" card)
 *           ?intro=moon → intro-star2.jpeg ("Moon" card)
 */
function IntroScene() {
  // Pick poster by URL param. Unknown value falls back to 'star' so
  // a typo doesn't produce a black panel during recording.
  const POSTERS: Record<string, string> = {
    star: '/intro/intro-star.jpeg',
    moon: '/intro/intro-star2.jpeg',
  };
  const variant = (() => {
    const v = new URLSearchParams(window.location.search).get('intro');
    return (v && POSTERS[v]) ? v : 'star';
  })();
  const posterSrc = POSTERS[variant];

  // Star particles — many more than before (28 vs 14) and they enter
  // earlier so Act I genuinely reads as "stars filling the sky" rather
  // than a thin trickle.
  const particles = useMemo(() => {
    return Array.from({ length: 28 }, (_, i) => {
      const goldenAngle = 137.508;
      const xStart = ((i * goldenAngle) % 100);
      const xDrift = -12 + ((i * 47) % 24);
      const size = 1.5 + ((i * 11) % 8) / 2.2;     // 1.5..5 px
      const duration = 6 + ((i * 3) % 5);          // 6..10s per pass
      const delayStagger = (i * 0.12) % 4;
      const opacityMax = 0.55 + ((i * 9) % 5) / 11;
      return { xStart, xDrift, size, duration, delayStagger, opacityMax };
    });
  }, []);

  // Reveal timing — all derived from a single anchor so they stay in sync.
  const REVEAL_START = 2.0;       // when the beam enters frame
  const REVEAL_DURATION = 3.8;    // top → bottom sweep

  return (
    <div
      className="relative mx-auto mb-6 overflow-hidden rounded-2xl border border-gold-500/30"
      style={{ width: 360, height: 640, background: '#05030f' }}
    >
      {/* Poster sits underneath everything. Fully loaded from t=0 so
          the reveal mask just has to retreat — no fade-in race. */}
      <img
        src={posterSrc}
        alt=""
        draggable={false}
        className="absolute inset-0 h-full w-full object-cover"
      />

      {/* Black reveal curtain. transformOrigin=bottom + scaleY 1→0
          means the curtain shrinks toward its bottom edge — its TOP
          retreats downward, revealing the poster's top first. Matches
          reading order: title (top) → card (middle) → subtitle (bottom).
          (If origin were 'top' the curtain would shrink upward, opening
          the BOTTOM first — wrong direction.) */}
      <motion.div
        className="absolute inset-0 bg-[#05030f] pointer-events-none"
        initial={{ scaleY: 1 }}
        animate={{ scaleY: 0 }}
        transition={{
          duration: REVEAL_DURATION,
          delay: REVEAL_START,
          ease: [0.65, 0, 0.35, 1],   // ease-in-out — slow start, slow end, brisk middle
        }}
        style={{ transformOrigin: 'bottom' }}
      />

      {/* The glowing beam — the "edge of light" that travels with the
          curtain. Sits at the exact y where the curtain ends. We achieve
          this by animating `top` over the same duration, so beam and
          curtain stay locked together. */}
      <motion.div
        className="absolute inset-x-0 pointer-events-none"
        initial={{ top: '0%', opacity: 0 }}
        animate={{
          top: ['0%', '100%'],
          opacity: [0, 1, 1, 0],
        }}
        transition={{
          duration: REVEAL_DURATION,
          delay: REVEAL_START,
          times: [0, 0.08, 0.92, 1],
          ease: [0.65, 0, 0.35, 1],
        }}
        style={{
          height: 80,
          marginTop: -40,             // centre the beam on its `top` y
          background:
            'linear-gradient(to bottom, transparent 0%, rgba(255,215,120,0.4) 35%, rgba(255,250,220,0.95) 50%, rgba(255,215,120,0.4) 65%, transparent 100%)',
          filter: 'blur(2px)',
          mixBlendMode: 'screen',
        }}
      />

      {/* Star particles — start invisible behind the curtain, fade up,
          drift upward forever. Visible THROUGH the curtain too so Act I
          shows them on black; once curtain retreats they keep going. */}
      {particles.map((p, i) => (
        <motion.div
          key={i}
          className="absolute pointer-events-none"
          style={{
            left: `${p.xStart}%`,
            bottom: 0,
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            background: 'rgba(255, 230, 160, 1)',
            boxShadow: '0 0 8px rgba(255, 220, 140, 0.95)',
            zIndex: 5,                // above curtain so Act I shows them
          }}
          initial={{ y: 0, opacity: 0, x: 0 }}
          animate={{
            y: -680,
            x: p.xDrift,
            opacity: [0, p.opacityMax, p.opacityMax, 0],
          }}
          transition={{
            duration: p.duration,
            delay: 0.2 + p.delayStagger,
            repeat: Infinity,
            ease: 'linear',
            times: [0, 0.15, 0.85, 1],
          }}
        />
      ))}

      {/* Gold breathing glow on the card. Starts AFTER the reveal so
          the card has time to "land", then loops forever. */}
      <motion.div
        className="absolute inset-0 pointer-events-none mix-blend-screen"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.6, 0.4, 0.6] }}
        transition={{
          duration: 4,
          delay: REVEAL_START + REVEAL_DURATION - 0.5,    // ~5.3s
          times: [0, 0.25, 0.6, 1],
          repeat: Infinity,
          repeatType: 'mirror',
          ease: 'easeInOut',
        }}
        style={{
          background:
            'radial-gradient(ellipse 45% 30% at 50% 52%, rgba(255,215,120,0.5) 0%, rgba(255,180,80,0.18) 40%, transparent 70%)',
        }}
      />
    </div>
  );
}


// ─── Scene: Spread (3-7 cards with cascade flip) ───────────────────────

function SpreadScene({ lang }: { lang: 'ru' | 'en' }) {
  const positions = lang === 'ru'
    ? ['Прошлое', 'Настоящее', 'Будущее']
    : ['Past', 'Present', 'Future'];

  const cards: CardEntry[] = [
    MAJORS.find((c) => c.name_en === 'The Chariot')!,
    MAJORS.find((c) => c.name_en === 'The High Priestess')!,
    MINORS.find((c) => c.name_en === 'Three of Cups')!,
  ];

  return (
    <OrnateFrame className="card-glow mb-6 rounded-2xl border border-gold-500/30 bg-night-900/40 px-4 py-6">
      <div className="flex flex-wrap items-end justify-center gap-4">
        {cards.map((card, i) => (
          <div key={i} className="flex flex-col items-center gap-2">
            <FlippableCard
              card={card}
              size={240}
              revealDelayMs={600 + i * 1000}
            />
            <span className="font-serif italic text-gold-300/80" style={{ fontSize: 14 }}>
              {i + 1}. {positions[i]}
            </span>
          </div>
        ))}
      </div>
    </OrnateFrame>
  );
}


// ─── Scene: Natal chart wheel ──────────────────────────────────────────

/**
 * Mock natal chart with realistic placements — picked so the wheel
 * looks visually rich: planets spread across multiple signs, several
 * juicy aspects, retrogrades on outer planets (the typical pattern).
 * Not a real person's chart; deliberately neutral.
 */
const MOCK_NATAL: NatalChartData = {
  birth_utc: '1990-04-15T07:30:00Z',
  lat: 55.7558,
  lon: 37.6173,
  planets: [
    { planet: 'sun',     longitude:  25,   sign: 'aries',       sign_degree: 25,   house: 1,  retrograde: false },
    { planet: 'moon',    longitude:  95,   sign: 'cancer',      sign_degree:  5,   house: 4,  retrograde: false },
    { planet: 'mercury', longitude: 358,   sign: 'pisces',      sign_degree: 28,   house: 12, retrograde: false },
    { planet: 'venus',   longitude:  40,   sign: 'taurus',      sign_degree: 10,   house: 2,  retrograde: false },
    { planet: 'mars',    longitude: 140,   sign: 'leo',         sign_degree: 20,   house: 5,  retrograde: false },
    { planet: 'jupiter', longitude: 252,   sign: 'sagittarius', sign_degree: 12,   house: 9,  retrograde: false },
    { planet: 'saturn',  longitude: 295,   sign: 'capricorn',   sign_degree: 25,   house: 10, retrograde: false },
    { planet: 'uranus',  longitude: 305,   sign: 'aquarius',    sign_degree:  5,   house: 11, retrograde: false },
    { planet: 'neptune', longitude: 348,   sign: 'pisces',      sign_degree: 18,   house: 12, retrograde: true  },
    { planet: 'pluto',   longitude: 232,   sign: 'scorpio',     sign_degree: 22,   house: 8,  retrograde: true  },
  ],
  houses: Array.from({ length: 12 }).map((_, i) => ({
    house: i + 1,
    longitude: (i * 30) % 360,
    sign: (['aries', 'taurus', 'gemini', 'cancer', 'leo', 'virgo',
            'libra', 'scorpio', 'sagittarius', 'capricorn', 'aquarius', 'pisces'] as const)[i],
    sign_degree: 0,
  })),
  ascendant: 0,
  midheaven: 270,
  aspects: [
    { planet_a: 'sun',     planet_b: 'moon',    aspect: 'square',      orb: 0.5, applying: true,  a_sign: 'aries', b_sign: 'cancer' },
    { planet_a: 'venus',   planet_b: 'pluto',   aspect: 'opposition',  orb: 1.2, applying: false, a_sign: 'taurus', b_sign: 'scorpio' },
    { planet_a: 'mercury', planet_b: 'neptune', aspect: 'conjunction', orb: 1.8, applying: true,  a_sign: 'pisces', b_sign: 'pisces' },
    { planet_a: 'mars',    planet_b: 'jupiter', aspect: 'trine',       orb: 2.0, applying: false, a_sign: 'leo', b_sign: 'sagittarius' },
    { planet_a: 'saturn',  planet_b: 'uranus',  aspect: 'conjunction', orb: 2.5, applying: true,  a_sign: 'capricorn', b_sign: 'aquarius' },
  ] as any,
  dominant_element: 'fire',
  dominant_modality: 'cardinal',
};

function NatalScene({ lang }: { lang: 'ru' | 'en' }) {
  const PLANET_LABELS_RU: Record<string, string> = {
    sun: 'Солнце', moon: 'Луна', mercury: 'Меркурий', venus: 'Венера',
    mars: 'Марс', jupiter: 'Юпитер', saturn: 'Сатурн', uranus: 'Уран',
    neptune: 'Нептун', pluto: 'Плутон',
  };
  const PLANET_LABELS_EN: Record<string, string> = {
    sun: 'Sun', moon: 'Moon', mercury: 'Mercury', venus: 'Venus',
    mars: 'Mars', jupiter: 'Jupiter', saturn: 'Saturn', uranus: 'Uranus',
    neptune: 'Neptune', pluto: 'Pluto',
  };
  const SIGN_LABELS_RU: Record<string, string> = {
    aries: 'Овен', taurus: 'Телец', gemini: 'Близнецы', cancer: 'Рак',
    leo: 'Лев', virgo: 'Дева', libra: 'Весы', scorpio: 'Скорпион',
    sagittarius: 'Стрелец', capricorn: 'Козерог', aquarius: 'Водолей',
    pisces: 'Рыбы',
  };
  const SIGN_LABELS_EN: Record<string, string> = {
    aries: 'Aries', taurus: 'Taurus', gemini: 'Gemini', cancer: 'Cancer',
    leo: 'Leo', virgo: 'Virgo', libra: 'Libra', scorpio: 'Scorpio',
    sagittarius: 'Sagittarius', capricorn: 'Capricorn', aquarius: 'Aquarius',
    pisces: 'Pisces',
  };
  const PLANET_GLYPHS: Record<string, string> = {
    sun: '☉', moon: '☽', mercury: '☿', venus: '♀', mars: '♂',
    jupiter: '♃', saturn: '♄', uranus: '♅', neptune: '♆', pluto: '♇',
  };

  return (
    <div className="flex flex-col items-center gap-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.2, ease: 'easeOut' }}
      >
        <NatalWheel chart={MOCK_NATAL} size={360} />
      </motion.div>

      {/* Planet list — slides in with cascade so the video reads as a
          living object, not a static screenshot. */}
      <OrnateFrame className="mb-2 w-full rounded-xl border border-gold-500/30 bg-night-900/40 px-4 py-3">
        <ul className="space-y-1.5 text-sm">
          {MOCK_NATAL.planets.map((p, i) => (
            <motion.li
              key={p.planet}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.2 + i * 0.12, duration: 0.4 }}
              className="flex items-center gap-3"
            >
              <span className="w-6 font-serif text-base text-gold-300">
                {PLANET_GLYPHS[p.planet]}
              </span>
              <span className="flex-1 font-serif text-gold-200">
                <span className="text-gold-300">
                  {lang === 'ru' ? PLANET_LABELS_RU[p.planet] : PLANET_LABELS_EN[p.planet]}
                </span>
                <span className="text-gold-400/70">
                  {' · '}
                  {lang === 'ru' ? SIGN_LABELS_RU[p.sign] : SIGN_LABELS_EN[p.sign]}
                </span>
                <span className="text-gold-400/60">
                  {' · '}
                  {p.house}{lang === 'ru' ? ' дом' : 'th house'}
                </span>
              </span>
              {p.retrograde && (
                <span className="text-[10px] uppercase tracking-widest text-rose-300/80">R</span>
              )}
            </motion.li>
          ))}
        </ul>
      </OrnateFrame>
    </div>
  );
}


// ─── Scene: Synastry partner planet table ──────────────────────────────

const MOCK_PARTNER_NAME = 'Андрей';
const MOCK_PARTNER_PLACE = 'Москва';
const MOCK_PARTNER_PLANETS = [
  { planet: 'sun',     sign: 'capricorn',   house: 5,  retrograde: false },
  { planet: 'moon',    sign: 'libra',       house: 3,  retrograde: false },
  { planet: 'mercury', sign: 'capricorn',   house: 5,  retrograde: false },
  { planet: 'venus',   sign: 'sagittarius', house: 4,  retrograde: false },
  { planet: 'mars',    sign: 'scorpio',     house: 3,  retrograde: false },
  { planet: 'jupiter', sign: 'cancer',      house: 11, retrograde: true  },
  { planet: 'saturn',  sign: 'aquarius',    house: 6,  retrograde: false },
  { planet: 'uranus',  sign: 'capricorn',   house: 5,  retrograde: false },
  { planet: 'neptune', sign: 'capricorn',   house: 5,  retrograde: false },
  { planet: 'pluto',   sign: 'scorpio',     house: 3,  retrograde: true  },
];

function SynastryScene({ lang }: { lang: 'ru' | 'en' }) {
  const PLANET_LABELS_RU: Record<string, string> = {
    sun: 'Солнце', moon: 'Луна', mercury: 'Меркурий', venus: 'Венера',
    mars: 'Марс', jupiter: 'Юпитер', saturn: 'Сатурн', uranus: 'Уран',
    neptune: 'Нептун', pluto: 'Плутон',
  };
  const PLANET_LABELS_EN: Record<string, string> = {
    sun: 'Sun', moon: 'Moon', mercury: 'Mercury', venus: 'Venus',
    mars: 'Mars', jupiter: 'Jupiter', saturn: 'Saturn', uranus: 'Uranus',
    neptune: 'Neptune', pluto: 'Pluto',
  };
  const SIGN_LABELS_RU: Record<string, string> = {
    aries: 'Овен', taurus: 'Телец', gemini: 'Близнецы', cancer: 'Рак',
    leo: 'Лев', virgo: 'Дева', libra: 'Весы', scorpio: 'Скорпион',
    sagittarius: 'Стрелец', capricorn: 'Козерог', aquarius: 'Водолей',
    pisces: 'Рыбы',
  };
  const SIGN_LABELS_EN: Record<string, string> = {
    aries: 'Aries', taurus: 'Taurus', gemini: 'Gemini', cancer: 'Cancer',
    leo: 'Leo', virgo: 'Virgo', libra: 'Libra', scorpio: 'Scorpio',
    sagittarius: 'Sagittarius', capricorn: 'Capricorn', aquarius: 'Aquarius',
    pisces: 'Pisces',
  };
  const PLANET_GLYPHS: Record<string, string> = {
    sun: '☉', moon: '☽', mercury: '☿', venus: '♀', mars: '♂',
    jupiter: '♃', saturn: '♄', uranus: '♅', neptune: '♆', pluto: '♇',
  };

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Summary chips — instant visual hook */}
      <motion.div
        className="grid w-full grid-cols-3 gap-2"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 px-2 py-2 text-center">
          <p className="text-xl font-serif text-sky-200">4</p>
          <p className="text-[9px] uppercase tracking-widest text-sky-300/70">
            {lang === 'ru' ? 'Гармония' : 'Harmony'}
          </p>
        </div>
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 px-2 py-2 text-center">
          <p className="text-xl font-serif text-rose-200">15</p>
          <p className="text-[9px] uppercase tracking-widest text-rose-300/70">
            {lang === 'ru' ? 'Вызовы' : 'Challenge'}
          </p>
        </div>
        <div className="rounded-lg border border-gold-500/30 bg-gold-500/5 px-2 py-2 text-center">
          <p className="text-xl font-serif text-gold-200">1</p>
          <p className="text-[9px] uppercase tracking-widest text-gold-300/70">
            {lang === 'ru' ? 'Соединения' : 'Conjunctions'}
          </p>
        </div>
      </motion.div>

      {/* Partner header */}
      <OrnateFrame className="w-full rounded-xl border border-gold-500/30 bg-night-900/40 px-4 py-2">
        <p className="text-center font-serif italic text-gold-200">
          {lang === 'ru' ? 'Ты и' : 'You and'}{' '}
          <span className="font-bold text-gold-100">{MOCK_PARTNER_NAME}</span>
        </p>
        <p className="mt-0.5 text-center text-[11px] text-gold-400/60">
          {MOCK_PARTNER_PLACE}
        </p>
      </OrnateFrame>

      {/* Partner planets table — cascade slide-in for video drama */}
      <OrnateFrame className="w-full rounded-xl border border-gold-500/30 bg-night-900/40 px-4 py-3">
        <p className="mb-2 text-[10px] uppercase tracking-widest text-gold-500/70">
          {lang === 'ru' ? `Карта ${MOCK_PARTNER_NAME} — планеты в знаках` : `${MOCK_PARTNER_NAME}'s chart — planets in signs`}
        </p>
        <ul className="space-y-1.5 text-sm">
          {MOCK_PARTNER_PLANETS.map((p, i) => (
            <motion.li
              key={p.planet}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + i * 0.1, duration: 0.4 }}
              className="flex items-center gap-3"
            >
              <span className="w-6 font-serif text-base text-gold-300">
                {PLANET_GLYPHS[p.planet]}
              </span>
              <span className="flex-1 font-serif text-gold-200">
                <span className="text-gold-300">
                  {lang === 'ru' ? PLANET_LABELS_RU[p.planet] : PLANET_LABELS_EN[p.planet]}
                </span>
                <span className="text-gold-400/70">
                  {' · '}
                  {lang === 'ru' ? SIGN_LABELS_RU[p.sign] : SIGN_LABELS_EN[p.sign]}
                </span>
                <span className="text-gold-400/60">
                  {' · '}
                  {p.house}{lang === 'ru' ? ' дом' : 'th house'}
                </span>
              </span>
              {p.retrograde && (
                <span className="text-[10px] uppercase tracking-widest text-rose-300/80">R</span>
              )}
            </motion.li>
          ))}
        </ul>
      </OrnateFrame>
    </div>
  );
}


// ─── Scene: Master's Room (AI chat) ────────────────────────────────────

/**
 * One-exchange AI-chat demo: user types, "Master" types back. Shows the
 * conversational nature of our AI tarot reader — competitors with
 * "tap-for-card" bots don't have this. Pre-canned messages chosen to
 * read as a natural intimate consultation, not a chatbot script.
 */
function MasterScene({ lang }: { lang: 'ru' | 'en' }) {
  const messages = lang === 'ru'
    ? {
        user: 'Стоит ли ему писать первой?',
        master: 'Карты говорят — Башня. Резкий шаг сейчас перевернёт то, что только начинает оседать. Подожди до завтрашнего вечера. Сама поймёшь — нужно или отпустить.',
      }
    : {
        user: 'Should I message him first?',
        master: 'The cards show the Tower. A sharp move now will overturn what\'s only just settling. Wait until tomorrow evening — you\'ll know on your own whether to or to let go.',
      };

  return (
    <OrnateFrame className="card-glow mb-6 rounded-2xl border border-gold-500/30 bg-night-900/40 px-4 py-6">
      <div className="flex flex-col gap-4">
        {/* User question — right-aligned chat bubble */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="flex justify-end"
        >
          <div className="max-w-[80%] rounded-2xl rounded-br-sm border border-gold-500/40
                          bg-gold-500/15 px-4 py-2.5">
            <p className="text-sm leading-snug text-gold-100">{messages.user}</p>
          </div>
        </motion.div>

        {/* Typing indicator — three dots pulsing in sequence */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 1, 0] }}
          transition={{ duration: 1.8, delay: 1.0, times: [0, 0.2, 0.85, 1] }}
          className="flex items-center gap-2"
        >
          <span className="text-base text-gold-300">✦</span>
          <div className="flex gap-1">
            {[0, 1, 2].map((i) => (
              <motion.span
                key={i}
                className="block h-1.5 w-1.5 rounded-full bg-gold-400"
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 1.0, repeat: Infinity, delay: i * 0.2 }}
              />
            ))}
          </div>
        </motion.div>

        {/* Master's response — left-aligned, with ✦ icon */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 2.8 }}
          className="flex items-start gap-2"
        >
          <span className="mt-1 text-base text-gold-400">✦</span>
          <div className="max-w-[85%] rounded-2xl rounded-bl-sm border border-gold-500/30
                          bg-night-800/80 px-4 py-2.5">
            <p className="font-serif italic leading-relaxed text-gold-200">
              {messages.master}
            </p>
          </div>
        </motion.div>
      </div>
    </OrnateFrame>
  );
}


// ─── Scene: Diary (reading history) ────────────────────────────────────

function DiaryScene({ lang }: { lang: 'ru' | 'en' }) {
  const insight = lang === 'ru'
    ? {
        title: 'Что я заметила за неделю',
        body: 'Карта Луна выпала у тебя дважды — на понедельник и пятницу. И обе раз — про одно и то же сомнение. Не торопись с решением до новолуния. Внутренний голос сейчас точнее логики.',
      }
    : {
        title: 'What I noticed this week',
        body: 'The Moon came up twice for you — Monday and Friday. Both times on the same doubt. Don\'t rush a decision before the new moon. Your inner voice is sharper than logic right now.',
      };

  const entries = lang === 'ru' ? [
    { date: 'Сегодня · 09:14',     kind: 'Карта дня',          icon: '☽', card: 'Звезда' },
    { date: 'Вчера · 21:08',       kind: 'Расклад на любовь',  icon: '♡', card: '5 карт' },
    { date: '5 июня · 08:30',      kind: 'Карта дня',          icon: '☽', card: 'Луна' },
    { date: '3 июня · 22:51',      kind: 'Кельтский крест',    icon: '✦', card: '10 карт' },
    { date: '1 июня · 09:02',      kind: 'Карта дня',          icon: '☽', card: 'Луна' },
    { date: '30 мая · 19:40',      kind: 'Мастер · вопрос',    icon: '◈', card: '«про работу»' },
  ] : [
    { date: 'Today · 09:14',       kind: 'Daily card',         icon: '☽', card: 'The Star' },
    { date: 'Yesterday · 21:08',   kind: 'Love spread',        icon: '♡', card: '5 cards' },
    { date: 'June 5 · 08:30',      kind: 'Daily card',         icon: '☽', card: 'The Moon' },
    { date: 'June 3 · 22:51',      kind: 'Celtic Cross',       icon: '✦', card: '10 cards' },
    { date: 'June 1 · 09:02',      kind: 'Daily card',         icon: '☽', card: 'The Moon' },
    { date: 'May 30 · 19:40',      kind: 'Master · question',  icon: '◈', card: '«about work»' },
  ];

  return (
    <div className="mb-6 flex flex-col gap-4">
      {/* Weekly AI-insight panel — the unique value-add over plain history */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <OrnateFrame className="card-glow rounded-2xl border border-gold-500/30 bg-night-900/40 px-4 py-3">
          <p className="mb-2 text-[10px] uppercase tracking-widest text-gold-500/70">
            ✦ {insight.title}
          </p>
          <p className="font-serif italic leading-relaxed text-gold-200">
            {insight.body}
          </p>
        </OrnateFrame>
      </motion.div>

      {/* Entries list — cascade slide-in. Mirrors Diary.tsx layout. */}
      <OrnateFrame className="rounded-2xl border border-gold-500/30 bg-night-900/40 px-4 py-3">
        <ul className="space-y-1.5 text-sm">
          {entries.map((entry, i) => (
            <motion.li
              key={i}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 + i * 0.12, duration: 0.4 }}
              className="flex items-center gap-3 rounded-lg border
                         border-gold-500/15 bg-night-900/40 px-3 py-2"
            >
              <span className="w-5 text-center text-base text-gold-300">
                {entry.icon}
              </span>
              <div className="flex-1">
                <p className="font-serif text-gold-200">{entry.kind}</p>
                <p className="text-[10px] uppercase tracking-widest text-gold-500/60">
                  {entry.date}
                </p>
              </div>
              <span className="text-xs italic text-gold-400/70">
                {entry.card}
              </span>
            </motion.li>
          ))}
        </ul>
      </OrnateFrame>
    </div>
  );
}


// ─── Demo language toggle (no reload) ──────────────────────────────────

void i18n;

function DemoLangToggle() {
  const { i18n } = useTranslation();
  const current: 'ru' | 'en' = i18n.language.startsWith('ru') ? 'ru' : 'en';

  const set = (lang: 'ru' | 'en') => {
    if (lang === current) return;
    try { localStorage.setItem('i18nextLng', lang); } catch {}
    i18n.changeLanguage(lang);
  };

  const cell = 'rounded-full px-3 py-1 text-xs font-medium transition';
  const active = 'bg-gold-500 text-night-900';
  const idle = 'text-gold-300/70 hover:text-gold-300';

  return (
    <div className="inline-flex items-center rounded-full border border-gold-500/30 bg-night-800/80 backdrop-blur">
      <button type="button" className={`${cell} ${current === 'ru' ? active : idle}`} onClick={() => set('ru')}>
        RU
      </button>
      <button type="button" className={`${cell} ${current === 'en' ? active : idle}`} onClick={() => set('en')}>
        EN
      </button>
    </div>
  );
}
